import * as i0 from '@angular/core';
import { Injectable, Directive, Input, Pipe, Self, HostListener, NgModule } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import * as i1$1 from '@angular/forms';
import { NG_VALIDATORS } from '@angular/forms';
import * as i1 from '@angular/platform-browser';
import { Subject, takeUntil, timer } from 'rxjs';
import { formatDate } from '@angular/common';

class AppMonitorService {
    constructor() {
        this.globalProperties = {};
    }
    init(sourceApplication, globalProperties) {
        let connString = window.applefcu?.applicationInsightsConnectionString;
        if (!connString)
            throw 'Application Insights connection string not set.';
        this.globalProperties = globalProperties || {};
        this.globalProperties['sourceApplication'] = sourceApplication;
        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: connString,
            }
        });
        this.appInsights?.loadAppInsights();
    }
    logEvent(name, customProperties) {
        this.appInsights?.trackEvent({ name: name }, this.appendGlobalProperties(customProperties));
    }
    logTrace(message, customProperties) {
        this.appInsights?.trackTrace({ message: message }, this.appendGlobalProperties(customProperties));
    }
    logException(exception, severityLevel, customProperties) {
        this.appInsights?.trackException({
            exception: exception,
            severityLevel: severityLevel,
        }, this.appendGlobalProperties(customProperties));
    }
    appendGlobalProperties(customProperties) {
        customProperties = customProperties || {};
        for (let prop in this.globalProperties) {
            customProperties[prop] = this.globalProperties[prop];
        }
        return customProperties;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AppMonitorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AppMonitorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AppMonitorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class GlobalErrorHandlerService {
    constructor(appMonitor) {
        this.appMonitor = appMonitor;
    }
    handleError(error) {
        console.error(error);
        this.appMonitor.logException(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: GlobalErrorHandlerService, deps: [{ token: AppMonitorService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: GlobalErrorHandlerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: GlobalErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: AppMonitorService }] });

class MaxValueDirective {
    constructor() {
        this.applefcuMaxValue = 0;
        this.maxValueErrorMessage = this.generateDefaultMessage();
        this.useDefaultError = true;
    }
    ngOnChanges(changes) {
        if (changes['maxValueErrorMessage']) {
            this.useDefaultError = false;
        }
        if (changes['applefcuMaxValue'] && this.useDefaultError) {
            this.maxValueErrorMessage = this.generateDefaultMessage();
        }
    }
    validate(control) {
        return control.value > this.applefcuMaxValue ?
            { maxValue: this.maxValueErrorMessage } : null;
    }
    generateDefaultMessage() {
        return `Please enter a value less than ${this.applefcuMaxValue}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MaxValueDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: MaxValueDirective, selector: "[applefcuMaxValue]", inputs: { applefcuMaxValue: "applefcuMaxValue", maxValueErrorMessage: "maxValueErrorMessage" }, providers: [{
                provide: NG_VALIDATORS,
                useExisting: MaxValueDirective,
                multi: true
            }], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MaxValueDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuMaxValue]',
                    providers: [{
                            provide: NG_VALIDATORS,
                            useExisting: MaxValueDirective,
                            multi: true
                        }]
                }]
        }], propDecorators: { applefcuMaxValue: [{
                type: Input
            }], maxValueErrorMessage: [{
                type: Input
            }] } });

class MinValueDirective {
    constructor() {
        this.applefcuMinValue = 0;
        this.minValueErrorMessage = this.generateDefaultMessage();
        this.useDefaultError = true;
    }
    ngOnChanges(changes) {
        if (changes['minValueErrorMessage']) {
            this.useDefaultError = false;
        }
        if (changes['applefcuMinValue'] && this.useDefaultError) {
            this.minValueErrorMessage = this.generateDefaultMessage();
        }
    }
    validate(control) {
        return control.value < this.applefcuMinValue ?
            { minValue: this.minValueErrorMessage } : null;
    }
    generateDefaultMessage() {
        return `Please enter a value greater than ${this.applefcuMinValue}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MinValueDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: MinValueDirective, selector: "[applefcuMinValue]", inputs: { applefcuMinValue: "applefcuMinValue", minValueErrorMessage: "minValueErrorMessage" }, providers: [{
                provide: NG_VALIDATORS,
                useExisting: MinValueDirective,
                multi: true
            }], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: MinValueDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuMinValue]',
                    providers: [{
                            provide: NG_VALIDATORS,
                            useExisting: MinValueDirective,
                            multi: true
                        }]
                }]
        }], propDecorators: { applefcuMinValue: [{
                type: Input
            }], minValueErrorMessage: [{
                type: Input
            }] } });

class RangeValidatorDirective {
    constructor() {
        this.minValue = 0;
        this.maxValue = 0;
        this.rangeErrorMessage = this.generateDefaultMessage();
        this.useDefaultError = true;
    }
    ngOnChanges(changes) {
        if (changes['rangeErrorMessage']) {
            this.useDefaultError = false;
        }
        if ((changes['minValue'] || changes['maxValue']) && this.useDefaultError) {
            this.rangeErrorMessage = this.generateDefaultMessage();
        }
    }
    validate(control) {
        return control.value > this.maxValue || control.value < this.minValue ?
            { range: this.rangeErrorMessage } : null;
    }
    generateDefaultMessage() {
        return `Please enter a value between ${this.minValue} and ${this.maxValue}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: RangeValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: RangeValidatorDirective, selector: "[applefcuRangeValidator]", inputs: { minValue: "minValue", maxValue: "maxValue", rangeErrorMessage: "rangeErrorMessage" }, providers: [{
                provide: NG_VALIDATORS,
                useExisting: RangeValidatorDirective,
                multi: true
            }], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: RangeValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuRangeValidator]',
                    providers: [{
                            provide: NG_VALIDATORS,
                            useExisting: RangeValidatorDirective,
                            multi: true
                        }]
                }]
        }], propDecorators: { minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], rangeErrorMessage: [{
                type: Input
            }] } });

class SanitizedHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: SanitizedHtmlPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.0.1", ngImport: i0, type: SanitizedHtmlPipe, name: "sanitizedHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: SanitizedHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitizedHtml'
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });

class UppercaseDirective {
    constructor(ngControl) {
        this.ngControl = ngControl;
        this.subNotifier = new Subject();
    }
    ngOnInit() {
        let control = this.ngControl.control;
        if (!control)
            return;
        control.valueChanges
            .pipe(takeUntil(this.subNotifier))
            .subscribe((data) => {
            let uppercaseVal = data?.toUpperCase();
            if (data && uppercaseVal !== data)
                control.setValue(uppercaseVal);
        });
    }
    ngOnDestroy() {
        this.subNotifier.next(undefined);
        this.subNotifier.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: UppercaseDirective, deps: [{ token: i1$1.NgControl, self: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: UppercaseDirective, selector: "[applefcuUppercase]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: UppercaseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuUppercase]'
                }]
        }], ctorParameters: () => [{ type: i1$1.NgControl, decorators: [{
                    type: Self
                }] }] });

class NumbersOnlyDirective {
    constructor(ngControl) {
        this.ngControl = ngControl;
        this.allowLeadingZeros = true;
        this.allowSingleZero = true;
        this.preventInvalidPaste = false;
        this.subNotifier = new Subject();
    }
    ngOnInit() {
        if (!this.control)
            return;
        this.control.valueChanges
            .pipe(takeUntil(this.subNotifier))
            .subscribe((data) => this.updateValue(data));
    }
    ngOnDestroy() {
        this.subNotifier.next(undefined);
        this.subNotifier.complete();
    }
    ngOnChanges(changes) {
        if (!changes['allowLeadingZeros'] && !changes['allowSingleZero'])
            return;
        // Bypass view change detection
        timer(0).subscribe(() => {
            if (!this.control)
                return;
            let val = this.control.value;
            this.updateValue(val);
        });
    }
    blockPaste(event) {
        const pasteData = event.clipboardData?.getData('text/plain');
        if (this.preventInvalidPaste && pasteData != this.filter(pasteData))
            event.preventDefault();
    }
    get control() {
        return this.ngControl.control;
    }
    updateValue(val) {
        let filteredVal = this.filter(val);
        // != is important here to weed out recursive null vs. undefined comparisons
        if (filteredVal != val)
            this.control?.setValue(filteredVal);
    }
    filter(str) {
        let matches = str?.match(/^\d*/g);
        if (!matches)
            return str;
        if (this.allowLeadingZeros || matches[0] === '')
            return matches[0];
        if (this.allowSingleZero && +matches[0] === 0)
            return '0';
        return `${+matches[0] || ''}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: NumbersOnlyDirective, deps: [{ token: i1$1.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: NumbersOnlyDirective, selector: "[applefcuNumbersOnly]", inputs: { allowLeadingZeros: "allowLeadingZeros", allowSingleZero: "allowSingleZero", preventInvalidPaste: "preventInvalidPaste" }, host: { listeners: { "paste": "blockPaste($event)" } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: NumbersOnlyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuNumbersOnly]'
                }]
        }], ctorParameters: () => [{ type: i1$1.NgControl }], propDecorators: { allowLeadingZeros: [{
                type: Input
            }], allowSingleZero: [{
                type: Input
            }], preventInvalidPaste: [{
                type: Input
            }], blockPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }] } });

class LettersOnlyDirective {
    constructor(ngControl) {
        this.ngControl = ngControl;
        this.preventInvalidPaste = false;
        this.allowSpace = true;
        this.allowHyphen = false;
        this.allowApostrophe = false;
        this.subNotifier = new Subject();
    }
    ngOnInit() {
        if (!this.control)
            return;
        this.control.valueChanges
            .pipe(takeUntil(this.subNotifier))
            .subscribe((data) => this.updateValue(data));
    }
    ngOnDestroy() {
        this.subNotifier.next(undefined);
        this.subNotifier.complete();
    }
    ngOnChanges(changes) {
        if (!changes['allowSpace'] && !changes['allowHypen'] && !changes['allowApostrophe'])
            return;
        // Bypass view change detection
        timer(0).subscribe(() => {
            if (!this.control)
                return;
            let val = this.control.value;
            this.updateValue(val);
        });
    }
    blockPaste(event) {
        const pasteData = event.clipboardData?.getData('text/plain');
        if (this.preventInvalidPaste && pasteData != this.filter(pasteData))
            event.preventDefault();
    }
    get control() {
        return this.ngControl.control;
    }
    get regex() {
        let expression = '^[a-zA-Z';
        if (this.allowSpace)
            expression += ' ';
        if (this.allowHyphen)
            expression += '\\-';
        if (this.allowApostrophe)
            expression += '\'';
        expression += ']*';
        return new RegExp(expression);
    }
    updateValue(val) {
        let filteredVal = this.filter(val);
        // != is important here to weed out recursive null vs. undefined comparisons
        if (filteredVal != val)
            this.control?.setValue(filteredVal);
    }
    filter(str) {
        let matches = str?.match(this.regex);
        if (!matches)
            return str;
        let trimmed = matches[0];
        // Don't want whitespace at start
        while (trimmed.length > 0 && trimmed[0] === ' ')
            trimmed = trimmed.slice(1);
        return trimmed;
    }
    onBlur() {
        this.control?.setValue(this.control.value != null ?
            this.control.value.trim() : this.control.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: LettersOnlyDirective, deps: [{ token: i1$1.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: LettersOnlyDirective, selector: "[applefcuLettersOnly]", inputs: { preventInvalidPaste: "preventInvalidPaste", allowSpace: "allowSpace", allowHyphen: "allowHyphen", allowApostrophe: "allowApostrophe" }, host: { listeners: { "blur": "onBlur()", "paste": "blockPaste($event)" } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: LettersOnlyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[applefcuLettersOnly]',
                    host: {
                        '(blur)': 'onBlur()'
                    }
                }]
        }], ctorParameters: () => [{ type: i1$1.NgControl }], propDecorators: { preventInvalidPaste: [{
                type: Input
            }], allowSpace: [{
                type: Input
            }], allowHyphen: [{
                type: Input
            }], allowApostrophe: [{
                type: Input
            }], blockPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }] } });

class ApplefcuUtilitiesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ApplefcuUtilitiesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.1", ngImport: i0, type: ApplefcuUtilitiesModule, declarations: [MaxValueDirective,
            MinValueDirective,
            RangeValidatorDirective,
            SanitizedHtmlPipe,
            UppercaseDirective,
            NumbersOnlyDirective,
            LettersOnlyDirective], exports: [MaxValueDirective,
            MinValueDirective,
            RangeValidatorDirective,
            SanitizedHtmlPipe,
            UppercaseDirective,
            NumbersOnlyDirective,
            LettersOnlyDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ApplefcuUtilitiesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ApplefcuUtilitiesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        MaxValueDirective,
                        MinValueDirective,
                        RangeValidatorDirective,
                        SanitizedHtmlPipe,
                        UppercaseDirective,
                        NumbersOnlyDirective,
                        LettersOnlyDirective
                    ],
                    imports: [],
                    exports: [
                        MaxValueDirective,
                        MinValueDirective,
                        RangeValidatorDirective,
                        SanitizedHtmlPipe,
                        UppercaseDirective,
                        NumbersOnlyDirective,
                        LettersOnlyDirective
                    ]
                }]
        }] });

class ValidatorConstants {
    static { this.symitarForbiddenChars = /[=?:&'~*^%@!#,.\-/\\]/; }
    static { this.alphaOnly = /^[a-zA-Z '\-]*$/; }
    static { this.numbersOnly = /^\d*$/; }
    static { this.alphaNumeric = /^[0-9a-zA-Z]*$/; }
    static { this.POBox = /\b(?:p\.?\s*o\.?|post\s+office)\s+box\b/i; }
}

class ApplefcuValidators {
    static get noSymitarForbiddenChars() {
        return (control) => {
            return ValidatorConstants.symitarForbiddenChars.test(control.value) ?
                { noSymitarForbiddenChars: { message: 'No special characters are allowed' } } : null;
        };
    }
    static get alphaOnly() {
        return (control) => {
            return !ValidatorConstants.alphaOnly.test(control.value) ?
                { alphaOnly: { message: 'Only alphabetical characters are allowed' } } : null;
        };
    }
    static get numbersOnly() {
        return (control) => {
            return control.value && !ValidatorConstants.numbersOnly.test(control.value) ?
                { numbersOnly: { message: 'Only numbers are allowed' } } : null;
        };
    }
    static get alphaNumericOnly() {
        return (control) => {
            return !ValidatorConstants.alphaNumeric.test(control.value) ?
                { alphaNumericOnly: { message: 'Only alphanumeric characters are allowed' } } : null;
        };
    }
    static get noPOBox() {
        return (control) => {
            return ValidatorConstants.POBox.test(control.value) ?
                { noPOBox: { message: 'PO Box is not allowed' } } : null;
        };
    }
    static get noForbiddenEmail() {
        return (control) => {
            if (control.value === null)
                return null;
            return (control.value.toUpperCase() === 'NONE@NONE.COM') ? { noForbiddenEmail: { message: 'Invalid email' } } : null;
        };
    }
    static get dateLessThanToday() {
        return (control) => {
            if (control.value === null || control.value.length === 0)
                return null;
            if (control.value.length < 10)
                return { invalidDate: { message: 'Invalid date' } };
            const date = formatDate(control.value, 'MM/dd/yyyy', 'en-us', 'utc');
            return (new Date(date) > new Date()) ? { dateLessThanToday: { message: 'Date cannot be in the future' } } : null;
        };
    }
    static get dateGreaterThanToday() {
        return (control) => {
            if (control.value === null || control.value.length === 0)
                return null;
            if (control.value.length < 10)
                return { invalidDate: { message: 'Invalid date' } };
            const date = formatDate(control.value, 'MM/dd/yyyy', 'en-us', 'utc');
            return (new Date(date) < new Date(new Date().toDateString())) ? { dateGreaterThanToday: { message: 'Date cannot be in the past' } } : null;
        };
    }
}

/*
 * Public API Surface of applefcu-utilities
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppMonitorService, ApplefcuUtilitiesModule, ApplefcuValidators, GlobalErrorHandlerService, LettersOnlyDirective, MaxValueDirective, MinValueDirective, NumbersOnlyDirective, RangeValidatorDirective, SanitizedHtmlPipe, UppercaseDirective, ValidatorConstants };
